import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faFilePdf, faSpinner, faExclamationTriangle, faArrowLeft,
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../utils/axiosConfig';
import PdfPreviewModal from './PdfPreviewModal';
import { fetchInvoiceDocuments } from '../../services/invoicingService';

/**
 * SupplierPdfChooserModal
 *
 * Surfaces a one-click "Preview Supplier PDF" experience for incoming invoices.
 *
 * Behaviour:
 *  - 0 PDFs attached -> friendly "no supplier PDF" message
 *  - 1 PDF attached  -> auto-opens it in PdfPreviewModal (no chooser shown)
 *  - 2+ PDFs         -> small chooser list; clicking a row previews that PDF.
 *                       A back button returns from the preview to the list.
 *
 * The Docuware download endpoint already does NOT write an audit log, so no
 * extra silencing is needed when this modal is used for preview.
 */
const SupplierPdfChooserModal = ({ isOpen, invoiceId, onClose, showError }) => {
  const [pdfDocs, setPdfDocs] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [listError, setListError] = useState(null);

  const [selectedDoc, setSelectedDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const previewUrlRef = useRef(null);

  const getFieldValue = (doc, fieldName) => {
    const field = (doc.Fields || []).find((f) => f.FieldName === fieldName);
    return field?.Item || null;
  };

  const isPdf = (filename) => {
    if (!filename) return false;
    return filename.split('.').pop().toLowerCase() === 'pdf';
  };

  const releasePreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
  }, []);

  const loadPreview = useCallback(async (doc) => {
    setIsLoadingPreview(true);
    try {
      const response = await axiosInstance.get(
        `/api/docuware/invoice-documents/download/${doc.Id}/`,
        { responseType: 'blob' }
      );
      releasePreviewUrl();
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' })
      );
      previewUrlRef.current = url;
      setPreviewUrl(url);
      setSelectedDoc(doc);
    } catch (error) {
      console.error('Failed to load supplier PDF preview:', error);
      if (showError) showError('Failed to load supplier PDF preview.');
    } finally {
      setIsLoadingPreview(false);
    }
  }, [releasePreviewUrl, showError]);

  const fetchPdfList = useCallback(async () => {
    if (!invoiceId) return;
    setIsLoadingList(true);
    setListError(null);
    try {
      const items = await fetchInvoiceDocuments(invoiceId);
      const pdfsOnly = items.filter((doc) => {
        const filename = getFieldValue(doc, 'SUBJECT');
        return isPdf(filename);
      });
      setPdfDocs(pdfsOnly);

      if (pdfsOnly.length === 1) {
        loadPreview(pdfsOnly[0]);
      }
    } catch (error) {
      console.error('Failed to fetch supplier PDFs:', error);
      setListError('Failed to load supplier documents.');
    } finally {
      setIsLoadingList(false);
    }
  }, [invoiceId, loadPreview]);

  useEffect(() => {
    if (isOpen && invoiceId) {
      fetchPdfList();
    }
    if (!isOpen) {
      setPdfDocs([]);
      setSelectedDoc(null);
      setListError(null);
      releasePreviewUrl();
    }
  }, [isOpen, invoiceId, fetchPdfList, releasePreviewUrl]);

  useEffect(() => () => releasePreviewUrl(), [releasePreviewUrl]);

  if (!isOpen) return null;

  if (previewUrl && selectedDoc) {
    const filename = getFieldValue(selectedDoc, 'SUBJECT') || 'supplier-invoice.pdf';
    const showBackButton = pdfDocs.length > 1;

    return (
      <>
        <PdfPreviewModal
          isOpen
          pdfUrl={previewUrl}
          fileName={filename}
          onClose={() => {
            if (showBackButton) {
              releasePreviewUrl();
              setSelectedDoc(null);
            } else {
              onClose();
            }
          }}
        />
        {showBackButton && (
          <div
            style={{
              position: 'fixed', top: '24px', left: '24px', zIndex: 10001,
            }}
          >
            <button
              type="button"
              onClick={() => {
                releasePreviewUrl();
                setSelectedDoc(null);
              }}
              style={{
                background: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '8px 14px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 500,
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Back to list
            </button>
          </div>
        )}
      </>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        style={{
          background: 'white', borderRadius: '10px',
          width: 'min(560px, 92vw)', maxHeight: '80vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px', borderBottom: '1px solid #e5e7eb',
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600 }}>
              Supplier PDFs
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: '#6b7280' }}>
              Choose a PDF to preview
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#6b7280', fontSize: '1.1rem', padding: '4px 8px',
            }}
            title="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
          {(isLoadingList || isLoadingPreview) && (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#6b7280' }}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '1.4rem', marginBottom: '0.6rem' }} />
              <p style={{ margin: 0 }}>{isLoadingPreview ? 'Loading PDF...' : 'Loading documents...'}</p>
            </div>
          )}

          {!isLoadingList && !isLoadingPreview && listError && (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#b91c1c' }}>
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }} />
              <p style={{ margin: 0 }}>{listError}</p>
            </div>
          )}

          {!isLoadingList && !isLoadingPreview && !listError && pdfDocs.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#6b7280' }}>
              <FontAwesomeIcon icon={faFilePdf} style={{ fontSize: '1.8rem', marginBottom: '0.5rem', opacity: 0.4 }} />
              <p style={{ margin: 0, fontWeight: 500 }}>No supplier PDF attached</p>
              <p style={{ margin: '0.4rem 0 0', fontSize: '0.85rem' }}>
                Upload one in the Documents panel to enable preview.
              </p>
            </div>
          )}

          {!isLoadingList && !isLoadingPreview && !listError && pdfDocs.length > 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {pdfDocs.map((doc) => {
                const filename = getFieldValue(doc, 'SUBJECT') || 'Unknown PDF';
                const supplierName = getFieldValue(doc, 'SUPPLIER_NAME');
                const supplierInvNo = getFieldValue(doc, 'SUPPLIER_INVOICE_NUMBER');
                return (
                  <button
                    key={doc.Id}
                    type="button"
                    onClick={() => loadPreview(doc)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '10px 12px', borderRadius: '8px',
                      border: '1px solid #e5e7eb', background: 'white',
                      cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faFilePdf}
                      style={{ color: '#dc2626', fontSize: '1.2rem', flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 500, fontSize: '0.92rem',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {filename}
                      </div>
                      {(supplierName || supplierInvNo) && (
                        <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '2px' }}>
                          {supplierName || ''}
                          {supplierName && supplierInvNo ? ' - ' : ''}
                          {supplierInvNo ? `Inv ${supplierInvNo}` : ''}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupplierPdfChooserModal;
